// ================================================================
// PLData.js — Data utilities for the P&L Submission Tool
// Tree is DB-driven (tblPL_LineDefinitions). This file provides:
//   - MONTHS, MONTH_NUMS constants
//   - buildTreeFromDefinitions() — flat DB rows → nested tree
//   - SEED_DEFINITIONS — used to populate tblPL_LineDefinitions on first run
// ================================================================

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const MONTH_NUMS = {
  Jan: 1, Feb: 2, Mar: 3,  Apr: 4,  May: 5,  Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

/**
 * Build nested tree from flat DB rows.
 * Normalises DB column names to the property names expected by PLViews:
 *   lineKey   → id
 *   lineType  → type
 *   isMajor   → major  (0/1 → false/true)
 *   isRequired → required
 *   isDeletable → deletable ('false'|'true'|'zero')
 *   isAccent  → accent  (0/1 → false/true)
 * @param {Array} rows — rows from tblPL_LineDefinitions, sorted by sortOrder
 * @returns {Array} root-level tree nodes
 */
export function buildTreeFromDefinitions(rows) {
  if (!rows || rows.length === 0) return [];

  const map = {};
  const sorted = [...rows].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  sorted.forEach((r) => {
    map[r.lineKey] = {
      id: r.lineKey,
      label: r.label,
      type: r.lineType,
      major: r.isMajor === 1 || r.isMajor === true,
      required: r.isRequired === 1 || r.isRequired === true,
      deletable: r.isDeletable,
      accent: r.isAccent === 1 || r.isAccent === true,
      formula: r.formula || '',
      values: {},
      children: [],
    };
  });

  const roots = [];
  sorted.forEach((r) => {
    if (!r.parentKey) {
      roots.push(map[r.lineKey]);
    } else if (map[r.parentKey]) {
      map[r.parentKey].children.push(map[r.lineKey]);
    }
  });

  return roots;
}

// ================================================================
// SEED_DEFINITIONS — flat list matching the full P&L hierarchy.
// Execute sql_queries.sql INSERT instead, but this is available
// for reference / unit testing.
// Only 5 master rows live in tblPL_LineDefinitions.
// All sub-items are configured by Super Admin via tblPL_CustomSchema.
// ================================================================
export const SEED_DEFINITIONS = [
  { lineKey: 'income',       parentKey: null, label: 'Total Revenue',       lineType: 'calculated', formula: '',                        isMajor: 1, isRequired: 1, isDeletable: 'false', isAccent: 0, sortOrder: 10 },
  { lineKey: 'cogs',         parentKey: null, label: 'Cost of Goods Sold', lineType: 'calculated', formula: '',                        isMajor: 1, isRequired: 1, isDeletable: 'false', isAccent: 0, sortOrder: 20 },
  { lineKey: 'gross_profit', parentKey: null, label: 'Gross Profit',   lineType: 'calculated', formula: 'income - cogs',           isMajor: 1, isRequired: 1, isDeletable: 'false', isAccent: 1, sortOrder: 30 },
  { lineKey: 'expenses',     parentKey: null, label: 'Total Expenses', lineType: 'calculated', formula: '',                        isMajor: 1, isRequired: 1, isDeletable: 'false', isAccent: 0, sortOrder: 40 },
  { lineKey: 'net_income',   parentKey: null, label: 'Net Income',     lineType: 'calculated', formula: 'gross_profit - expenses', isMajor: 1, isRequired: 1, isDeletable: 'false', isAccent: 1, sortOrder: 50 },
];
